import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import './AdminDasboard.css';
import {
  Users, Timer,
  RefreshCw, TrendingUp, TrendingDown,
  User, Activity, Calendar, Clock, Target, LogIn, LogOut,
  Hourglass, XCircle, LayoutDashboard, Megaphone,
  Briefcase, Package, Umbrella, UserCog, FolderOpen,
  FileText, Shield, Wallet, Fingerprint, Receipt, Settings, Handshake, Mail, Building2, Bell, ArrowLeft
} from 'lucide-react';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

function HRDashboard() {
  const { user, hasPermission, logout, notifications, unreadNotificationsCount } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  // Notification counts
  const unreadTaskNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Task').length || 0;
  const unreadAnnouncementNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Announcement').length || 0;
  const unreadLeaveNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Leave').length || 0;
  const unreadClientNotificationsCount = notifications?.filter(n => !n.isRead && n.type === 'Client').length || 0;
  const [unviewedBroadcasts, setUnviewedBroadcasts] = useState(0);

  useEffect(() => {
    const fetchUnviewedCounts = async () => {
      try {
        const broadcastsRes = await api.get("/broadcasts/unviewed/count");
        setUnviewedBroadcasts(broadcastsRes.data.count);
      } catch (error) {
        console.error("Error fetching unviewed counts:", error);
      }
    };
    fetchUnviewedCounts();
    const interval = setInterval(fetchUnviewedCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const renderBadge = (count) => {
    if (!count || count <= 0) return null;
    return (
      <span style={{
        position: 'absolute', top: '12px', right: '12px',
        background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 'bold',
        padding: '2px 6px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
      }}>
        {count > 99 ? '99+' : count}
      </span>
    );
  };
  const tileStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    background: '#fff', padding: '28px 16px', borderRadius: '16px', border: '1px solid #e2e8f0',
    textDecoration: 'none', color: '#0f172a', transition: 'all 0.2s ease',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', cursor: 'pointer', minHeight: '140px'
  };
  const tileTextStyle = { fontWeight: '600', fontSize: '15px', textAlign: 'center' };
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState(location.state?.returnToView || 'main');
  // Check-in/out states
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [checkInTimer, setCheckInTimer] = useState(0);
  // --- BREAK LOGIC STATES ---
  const [activeBreak, setActiveBreak] = useState(null);
  const [hideBreakScreen, setHideBreakScreen] = useState(false);
  const [breakTimer, setBreakTimer] = useState(0);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakForm, setBreakForm] = useState({ type: 'Tea Break', reason: '' });
  const [breakDetailsModal, setBreakDetailsModal] = useState({ visible: false, type: '', breaks: [] });
  const [breakLimits, setBreakLimits] = useState({ totalBreakLimit: 60 });

  useEffect(() => {
    api.get('/attendance/break-limits').then(res => {
      if (res.data) {
        setBreakLimits({
          totalBreakLimit: res.data.totalBreakLimit ?? 60
        });
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    let interval;
    if (activeBreak && activeBreak.startTime && !activeBreak.endTime) {
      const updateBreakTimer = () => {
        const elapsed = Math.floor((Date.now() - new Date(activeBreak.startTime).getTime()) / 1000);
        setBreakTimer(elapsed);
      };
      updateBreakTimer();
      interval = setInterval(updateBreakTimer, 1000);
    } else {
      setBreakTimer(0);
    }
    return () => clearInterval(interval);
  }, [activeBreak]);

  const checkInIntervalRef = useRef(null);
  const checkedInThisSession = useRef(false);
  const [stats, setStats] = useState({
    presentToday: 0,
    absentToday: 0,
    activeProjects: 0,
    totalLeads: 0,
    totalActive: 0,
  });
  // Previous period values for dynamic percentage calculation
  const [prevStats, setPrevStats] = useState({
    presentToday: 0,
    absentToday: 0,
    activeProjects: 0,
    totalLeads: 0,
  });
  const [departmentData, setDepartmentData] = useState({ departments: [], counts: [], maleCounts: [], femaleCounts: [] });
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [projectStatusCounts, setProjectStatusCounts] = useState({ completed: 0, working: 0, canceled: 0 });
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentGoals, setRecentGoals] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const isMounted = useRef(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const monthPickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) {
        setShowMonthPicker(false);
      }
    };
    if (showMonthPicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMonthPicker]);

  // Lead sources — populated from meta-leads/source-stats
  const [leadSources, setLeadSources] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  // Timer: always run from loginTime so it survives page reloads
  // Rules: only Lunch/Tea breaks pause this timer; Other breaks do not.
  useEffect(() => {
    if (todayAttendance?.loginTime) {
      const updateTimer = () => {
        const endTime = todayAttendance.logoutTime ? new Date(todayAttendance.logoutTime) : new Date();
        let elapsed = Math.floor((endTime - new Date(todayAttendance.loginTime)) / 1000);
        let deductSecs = 0;

        if (todayAttendance.breaks && todayAttendance.breaks.length > 0) {
          todayAttendance.breaks.forEach(b => {
            if (b.breakType === 'Other') return; // ignore Other breaks

            let breakDur = 0;
            if (b.endTime) {
              breakDur = Math.round((new Date(b.endTime) - new Date(b.startTime)) / 1000);
            } else {
              // Active: pause by subtracting full elapsed
              const breakEnd = todayAttendance.logoutTime ? new Date(todayAttendance.logoutTime) : new Date();
              breakDur = Math.round((breakEnd - new Date(b.startTime)) / 1000);
            }
            deductSecs += breakDur;
          });
        }

        setCheckInTimer(Math.max(0, elapsed - deductSecs));
      };

      updateTimer();
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
      }
      if (!todayAttendance.logoutTime) {
        checkInIntervalRef.current = setInterval(updateTimer, 1000);
      }
    } else {
      setCheckInTimer(0);
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
    }
    return () => {
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
    };
  }, [todayAttendance, activeBreak, breakLimits]);

  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    if (selectedMonth) fetchDepartmentData(selectedMonth);
  }, [selectedMonth]);

  useEffect(() => {
    if (!loading && !chartLoading && departmentData.departments.length > 0) renderChart();
    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, [departmentData, loading, chartLoading]);

  const fetchDepartmentData = async (month) => {
    setChartLoading(true);
    try {
      const genderStats = await api.get(`/employees/gender-stats?month=${month}`)
        .catch(() => ({ data: { departments: [], overall: { total: 0, male: 0, female: 0 } } }));
      const depts = genderStats.data?.departments || [];
      const newData = {
        departments: depts.map(d => d.name),
        counts: depts.map(d => d.total),
        maleCounts: depts.map(d => d.male),
        femaleCounts: depts.map(d => d.female),
      };
      setDepartmentData(newData);
      setChartLoading(false);
      // Call renderChart directly with fresh data to avoid stale closure
      setTimeout(() => renderChart(newData), 0);
    } catch {
      setChartLoading(false);
    }
  };


  const fetchTodayAttendance = async () => {
    try {
      const response = await api.get('/attendance/today');
      if (response.data) {
        setTodayAttendance(response.data);
        if (response.data.breaks && response.data.breaks.length > 0) {
          const ongoingBreak = response.data.breaks.find(b => !b.endTime);
          setActiveBreak(ongoingBreak || null);
        } else {
          setActiveBreak(null);
        }
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setTodayAttendance(null);
    }
  };

  const handleBreakStart = async (e) => {
    setHideBreakScreen(false);
    e.preventDefault();
    if (breakForm.type === 'Other' && !breakForm.reason.trim()) {
      alert('Please enter a reason for your break.');
      return;
    }
    try {
      const response = await api.post('/attendance/break/start', {
        breakType: breakForm.type,
        reason: breakForm.reason
      });
      if (response.data?.break) {
        setActiveBreak(response.data.break);
        setShowBreakModal(false);
        setBreakForm({ type: 'Tea Break', reason: '' });
        await fetchTodayAttendance();
      }
    } catch (error) {
      console.error('Error starting break:', error);
      alert(error.response?.data?.message || 'Failed to start break. Please try again.');
    }
  };

  const handleBreakEnd = async () => {
    try {
      await api.post('/attendance/break/end');
      setActiveBreak(null);
      setBreakTimer(0);
      await fetchTodayAttendance();
      alert('Break ended successfully.');
    } catch (error) {
      console.error('Error ending break:', error);
      alert(error.response?.data?.message || 'Failed to end break. Please try again.');
    }
  };

  const getTotalBreakDurationString = () => {
    let totalSecs = 0;
    if (todayAttendance?.breaks) {
      todayAttendance.breaks.forEach(b => {
        if (b.duration) {
          totalSecs += b.duration * 60;
        } else if (b.startTime && !b.endTime) {
          totalSecs += Math.floor((Date.now() - new Date(b.startTime).getTime()) / 1000);
        }
      });
    }
    if (activeBreak) {
      const inArray = todayAttendance?.breaks?.some(b => b.startTime === activeBreak.startTime);
      if (!inArray) {
        totalSecs += Math.floor((Date.now() - new Date(activeBreak.startTime).getTime()) / 1000);
      }
    }
    if (totalSecs === 0) return '0 mins';
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    if (hrs > 0) return `${hrs} hr ${mins} mins`;
    return `${mins} mins`;
  };

  const handleCheckIn = async () => {
    try {
      // Mark as checked in this session and start timer for UI feedback
      checkedInThisSession.current = true;
      setCheckInTimer(0);
      if (!checkInIntervalRef.current) {
        checkInIntervalRef.current = setInterval(() => {
          setCheckInTimer(prev => prev + 1);
        }, 1000);
      }
      const attendanceData = { method: 'self-checkin' };

      // Location is required for geofenced check-in
      if (!navigator.geolocation) {
        checkedInThisSession.current = false;
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
        setCheckInTimer(0);
        alert('Location access is not supported by your browser. Check-in requires location.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`
          };
          api.post('/attendance/mark', { ...attendanceData, location: locationData })
            .then(response => {
              if (response.data) {
                setTodayAttendance(response.data);
                alert('Check-in successful!');
                // Refresh stats to update present count
                fetchAll();
              }
            })
            .catch(err => {
              checkedInThisSession.current = false;
              clearInterval(checkInIntervalRef.current);
              checkInIntervalRef.current = null;
              setCheckInTimer(0);
              const msg = err.response?.data?.message || '';
              if (msg.toLowerCase().includes('away') || msg.toLowerCase().includes('office')) {
                alert('You can only check in inside the office.');
              } else {
                alert(msg || 'Failed to check in. Please try again.');
              }
            });
        },
        (geoErr) => {
          checkedInThisSession.current = false;
          clearInterval(checkInIntervalRef.current);
          checkInIntervalRef.current = null;
          setCheckInTimer(0);
          alert('Please give permission for location to check in.');
        },
        { timeout: 8000, enableHighAccuracy: true, maximumAge: 0 }
      );
    } catch (error) {
      console.error('Error checking in:', error);
      checkedInThisSession.current = false;
      clearInterval(checkInIntervalRef.current);
      checkInIntervalRef.current = null;
      setCheckInTimer(0);
      alert(error.response?.data?.message || 'Failed to check in. Please try again.');
    }
  };

  const handleCheckOut = async () => {
    try {
      // Stop timer and clear session flag
      checkedInThisSession.current = false;
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
      setCheckInTimer(0);

      if (!navigator.geolocation) {
        alert('Location access is not supported by your browser. Check-out requires location.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`
          };
          api.post('/attendance/checkout', { location: locationData })
            .then(response => {
              if (response.data) {
                setTodayAttendance(response.data);
                alert(`Checkout successful! Working Hours: ${response.data.workingHours} hrs | Status: ${response.data.status}`);
                // Refresh stats to update present/absent counts
                fetchAll();
              }
            })
            .catch(err => {
              const msg = err.response?.data?.message || '';
              if (msg.toLowerCase().includes('away') || msg.toLowerCase().includes('office')) {
                alert('You can only check out inside the office.');
              } else {
                alert(msg || 'Failed to check out. Please try again.');
              }
            });
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Please give permission for location to check out.');
        },
        { timeout: 8000, enableHighAccuracy: true, maximumAge: 0 }
      );
    } catch (error) {
      console.error('Error checking out:', error);
      alert(error.response?.data?.message || 'Failed to check out. Please try again.');
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch today's attendance first
      await fetchTodayAttendance();
      // Fetch current and previous stats in parallel (adjust endpoint as needed)
      const [attendanceStats, projects, metaLeadsResp, genderStats, prevStatsResp] = await Promise.all([
        api.get('/attendance/today-stats').catch(() => ({ data: { presentToday: 0, absentToday: 0 } })),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/meta-leads?limit=10').catch(() => ({ data: { leads: [], total: 0 } })),
        api.get(`/employees/gender-stats?month=${selectedMonth}`).catch(() => ({ data: { departments: [], overall: { total: 0, male: 0, female: 0 } } })),
        api.get('/dashboard/previous-stats').catch(() => ({ data: { presentToday: 0, absentToday: 0, activeProjects: 0, totalLeads: 0 } })),
      ]);

      // Use meta-leads response for the Total Leads count
      const leadsArr = metaLeadsResp.data?.leads || (Array.isArray(metaLeadsResp.data) ? metaLeadsResp.data : []);
      const totalMetaLeads = metaLeadsResp.data?.pagination?.total ?? metaLeadsResp.data?.total ?? (Array.isArray(metaLeadsResp.data) ? metaLeadsResp.data.length : leadsArr.length);

      // Fetch recent activities
      const activitiesResp = await api.get('/activities').catch(() => ({ data: [] }));
      const activitiesData = Array.isArray(activitiesResp.data) ? activitiesResp.data : [];
      setRecentActivities(activitiesData);

      // Fetch today's goals
      const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
      const goalsResp = await api.get(`/goals?startDate=${todayStart.toISOString()}&endDate=${todayEnd.toISOString()}&limit=1000`).catch(() => ({ data: [] }));
      const goalsData = Array.isArray(goalsResp.data) ? goalsResp.data : [];
      const sortedGoals = [...goalsData].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setRecentGoals(sortedGoals);

      // Fetch lead source stats
      const GAUGE_COLORS = ['#162a16', '#056098', '#1EA7FF', '#0891b2', '#8b5cf6', '#f59e0b'];
      const sourceStatsResp = await api.get('/meta-leads/source-stats').catch(() => ({ data: { sources: [] } }));
      const rawSources = sourceStatsResp.data?.sources || [];
      setLeadSources(rawSources.map((s, i) => ({
        label: s.label,
        color: GAUGE_COLORS[i % GAUGE_COLORS.length],
        value: s.percent,
        count: s.count,
      })));

      // Count project statuses for pie chart
      let completed = 0, working = 0, canceled = 0;
      (projects.data || []).forEach(p => {
        if (['completed', 'done', 'finished'].includes((p.status || '').toLowerCase())) completed++;
        else if (['active', 'in-progress', 'working', 'progress'].includes((p.status || '').toLowerCase())) working++;
        else if (['canceled', 'cancelled', 'aborted'].includes((p.status || '').toLowerCase())) canceled++;
      });
      setProjectStatusCounts({ completed, working, canceled });

      const activeProjects = (projects.data || []).filter(
        p => p.status === 'active' || p.status === 'on-hold'
      ).length;

      setStats({
        presentToday: attendanceStats.data?.presentToday || 0,
        absentToday: attendanceStats.data?.absentToday || 0,
        activeProjects,
        totalLeads: totalMetaLeads,
        totalActive: attendanceStats.data?.totalActive || 0,
      });

      // Set previous stats for percentage calculation
      setPrevStats({
        presentToday: prevStatsResp.data?.presentToday || 0,
        absentToday: prevStatsResp.data?.absentToday || 0,
        activeProjects: prevStatsResp.data?.activeProjects || 0,
        totalLeads: prevStatsResp.data?.totalLeads || 0,
      });

      const depts = genderStats.data?.departments || [];
      setDepartmentData({
        departments: depts.map(d => d.name),
        counts: depts.map(d => d.total),
        maleCounts: depts.map(d => d.male),
        femaleCounts: depts.map(d => d.female),
      });
      // Prevent the selectedMonth useEffect from double-fetching on initial load
      // by setting departmentData here directly with the initial month
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const renderChart = (data) => {
    const chartData = data || departmentData;
    if (!chartRef.current || !window.Chart) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext('2d');
    const canvasHeight = chartRef.current.offsetHeight || 320;

    // Dynamic y-axis: spread the data to fill the chart height naturally
    const allVals = [...(chartData.counts || []), ...(chartData.maleCounts || []), ...(chartData.femaleCounts || [])];
    const dataMax = Math.max(...allVals, 1);
    const rawStep = dataMax <= 5 ? 1 : dataMax <= 20 ? 5 : dataMax <= 50 ? 10 : dataMax <= 100 ? 20 : 50;
    const suggestedMax = Math.ceil((dataMax * 1.6) / rawStep) * rawStep;

    // Strong gradient fills — bold wave style like the reference
    const makeGrad = (r, g, b, topAlpha, botAlpha) => {
      const g1 = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      g1.addColorStop(0, `rgba(${r},${g},${b},${topAlpha})`);
      g1.addColorStop(0.45, `rgba(${r},${g},${b},${topAlpha * 0.5})`);
      g1.addColorStop(1, `rgba(${r},${g},${b},${botAlpha})`);
      return g1;
    };

    const crosshairPlugin = {
      id: 'crosshair',
      afterDraw(chart) {
        if (!chart.tooltip._active || !chart.tooltip._active.length) return;
        const c = chart.ctx;
        const x = chart.tooltip._active[0].element.x;
        c.save();
        c.beginPath();
        c.moveTo(x, chart.scales.y.top);
        c.lineTo(x, chart.scales.y.bottom);
        c.lineWidth = 1;
        c.strokeStyle = 'rgba(107,114,128,0.3)';
        c.setLineDash([5, 4]);
        c.stroke();
        c.restore();
      },
    };

    chartInstance.current = new window.Chart(ctx, {
      type: 'line',
      plugins: [crosshairPlugin],
      data: {
        labels: chartData.departments,
        datasets: [
          {
            label: 'Total',
            data: chartData.counts,
            borderColor: '#b2ff1e',
            backgroundColor: makeGrad(178, 255, 0, 0.55, 0.02),
            borderWidth: 2.5,
            tension: 0.5,
            cubicInterpolationMode: 'monotone',
            fill: 'origin',
            order: 3,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#b2ff1e',
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 2,
          },
          {
            label: 'Male',
            data: chartData.maleCounts,
            borderColor: '#056098',
            backgroundColor: makeGrad(59, 119, 94, 0.50, 0.02),
            borderWidth: 2.5,
            tension: 0.5,
            cubicInterpolationMode: 'monotone',
            fill: 'origin',
            order: 2,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#056098',
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 2,
          },
          {
            label: 'Female',
            data: chartData.femaleCounts,
            borderColor: '#ff9900',
            backgroundColor: makeGrad(255, 153, 0, 0.45, 0.02),
            borderWidth: 2.5,
            tension: 0.5,
            cubicInterpolationMode: 'monotone',
            fill: 'origin',
            order: 1,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#ff9900',
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 8, right: 4, bottom: 0, left: 0 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#ffffff',
            padding: { top: 10, bottom: 10, left: 14, right: 16 },
            titleColor: '#111827',
            bodyColor: '#4b5563',
            titleFont: { size: 13, weight: '700', family: 'Inter, Segoe UI, Arial, sans-serif' },
            bodyFont: { size: 12, weight: '400', family: 'Inter, Segoe UI, Arial, sans-serif' },
            cornerRadius: 10,
            borderColor: '#e5e7eb',
            borderWidth: 1,
            displayColors: true,
            boxWidth: 8,
            boxHeight: 8,
            boxPadding: 5,
            caretSize: 5,
            callbacks: {
              title: (items) => items[0].label,
              label: (item) => `  ${item.dataset.label}: ${item.parsed.y}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax,
            ticks: {
              font: { size: 11, family: 'Inter, Segoe UI, Arial, sans-serif' },
              color: '#c4c9d4',
              stepSize: rawStep,
              padding: 12,
              maxTicksLimit: 5,
            },
            grid: {
              display: true,
              color: 'rgba(229,231,235,0.55)',
              lineWidth: 1,
            },
            border: { display: false },
          },
          x: {
            ticks: {
              font: { size: 11, family: 'Inter, Segoe UI, Arial, sans-serif' },
              color: '#c4c9d4',
              maxRotation: 0,
              minRotation: 0,
              padding: 8,
            },
            grid: { display: false },
            border: { display: false },
          },
        },
        interaction: { intersect: false, mode: 'index' },
        animation: { duration: 800, easing: 'easeInOutCubic' },
      },
    });
  };

  // Dynamic gauge arc helpers
  const gaugeArcPoint = (frac) => {
    const angle = Math.PI * (1 - frac);
    return [+(50 + 40 * Math.cos(angle)).toFixed(3), +(50 - 40 * Math.sin(angle)).toFixed(3)];
  };
  const gaugeArcPath = (fromFrac, toFrac) => {
    if (toFrac - fromFrac < 0.001) return '';
    const [x1, y1] = gaugeArcPoint(fromFrac);
    const [x2, y2] = gaugeArcPoint(toFrac);
    // The full gauge is only 180° (a semicircle), so any segment within it
    // is always ≤180° — large-arc-flag must always be 0.
    return `M${x1},${y1} A40,40 0 0,1 ${x2},${y2}`;
  };

  const formatTimeAgo = (dateStr) => {
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const activityIconMap = {
    leave: { color: '#f59e0b', bg: '#fffbeb' },
    attendance: { color: '#16a34a', bg: '#f0fdf4' },
    project: { color: '#3b82f6', bg: '#eff6ff' },
    lead: { color: '#8b5cf6', bg: '#f5f3ff' },
    payroll: { color: '#0891b2', bg: '#ecfeff' },
    employee: { color: '#056098', bg: '#f0faf5' },
    default: { color: '#6b7280', bg: '#f3f4f6' },
  };

  const getActivityStyle = (type = '') => {
    const t = type.toLowerCase();
    const key = Object.keys(activityIconMap).find(k => t.includes(k)) || 'default';
    return activityIconMap[key];
  };

  const getActivityIcon = (type = '', color) => {
    const t = type.toLowerCase();
    if (t.includes('leave'))
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="18" rx="3" fill="#fbbf24" opacity="0.18" />
          <rect x="3" y="4" width="18" height="18" rx="3" stroke="#f59e0b" strokeWidth="1.5" fill="none" />
          <line x1="16" y1="2" x2="16" y2="6" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="8" y1="2" x2="8" y2="6" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="3" y1="10" x2="21" y2="10" stroke="#f59e0b" strokeWidth="1.3" />
          <rect x="7" y="13" width="3" height="3" rx="0.8" fill="#f59e0b" />
          <rect x="11" y="13" width="3" height="3" rx="0.8" fill="#fcd34d" />
          <rect x="7" y="17" width="3" height="2" rx="0.8" fill="#fcd34d" />
        </svg>
      );
    if (t.includes('attendance'))
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" fill="#16a34a" opacity="0.14" />
          <circle cx="12" cy="12" r="9" stroke="#16a34a" strokeWidth="1.5" fill="none" />
          <circle cx="12" cy="12" r="2" fill="#16a34a" />
          <line x1="12" y1="7" x2="12" y2="12" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" />
          <line x1="12" y1="12" x2="15.5" y2="14.5" stroke="#4ade80" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    if (t.includes('project'))
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M3 7c0-1.1.9-2 2-2h4l2 2.5h8a2 2 0 0 1 2 2V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" fill="#3b82f6" opacity="0.18" />
          <path d="M3 7c0-1.1.9-2 2-2h4l2 2.5h8a2 2 0 0 1 2 2V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="#3b82f6" strokeWidth="1.5" fill="none" />
          <line x1="8" y1="13" x2="16" y2="13" stroke="#60a5fa" strokeWidth="1.4" strokeLinecap="round" />
          <line x1="8" y1="16" x2="13" y2="16" stroke="#93c5fd" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    if (t.includes('lead'))
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="9" width="20" height="6" rx="3" fill="#8b5cf6" opacity="0.12" />
          <polyline points="2 12 5.5 12 8 5 12 19 15 9 17.5 12 22 12" stroke="#8b5cf6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <circle cx="8" cy="5" r="1.2" fill="#a78bfa" />
          <circle cx="15" cy="9" r="1.2" fill="#a78bfa" />
        </svg>
      );
    if (t.includes('payroll'))
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" fill="#0891b2" opacity="0.13" />
          <circle cx="12" cy="12" r="9" stroke="#0891b2" strokeWidth="1.5" fill="none" />
          <line x1="12" y1="6" x2="12" y2="18" stroke="#0891b2" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M15 9H10a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8" stroke="#22d3ee" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        </svg>
      );
    if (t.includes('employee') || t.includes('joined') || t.includes('team') || t.includes('user'))
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="3.5" fill="#056098" opacity="0.2" />
          <circle cx="12" cy="8" r="3.5" stroke="#056098" strokeWidth="1.5" fill="none" />
          <path d="M5 20c0-3.87 3.13-7 7-7s7 3.13 7 7" stroke="#056098" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          <path d="M5 20c0-3.87 3.13-7 7-7s7 3.13 7 7" stroke="#056098" strokeWidth="1.5" strokeLinecap="round" fill="#056098" opacity="0.1" />
        </svg>
      );
    if (t.includes('goal'))
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="#f97316" strokeWidth="1.5" fill="#fed7aa" opacity="0.9" fillOpacity="0.2" />
          <circle cx="12" cy="12" r="6" stroke="#f97316" strokeWidth="1.3" fill="none" />
          <circle cx="12" cy="12" r="3" stroke="#f97316" strokeWidth="1.2" fill="#fed7aa" fillOpacity="0.4" />
          <circle cx="12" cy="12" r="1.5" fill="#f97316" />
        </svg>
      );
    // default: info
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" fill="#6b7280" opacity="0.12" />
        <circle cx="12" cy="12" r="9" stroke="#6b7280" strokeWidth="1.5" fill="none" />
        <circle cx="12" cy="8" r="1" fill="#6b7280" />
        <line x1="12" y1="11" x2="12" y2="17" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAll();
    setRefreshing(false);
  };

  // Helper to calculate percent change and arrow
  // Accepts a themeColor for positive values
  function getPercentChange(current, prev, themeColor = '#16a34a', themeBg = 'rgba(22,163,74,0.08)') {
    if (prev === 0 && current === 0) return { percent: '0%', color: '#6b7280', bg: '#f3f4f6', arrow: 'up' };
    if (prev === 0) return { percent: '+100%', color: themeColor, bg: themeBg, arrow: 'up' };
    const diff = current - prev;
    const pct = Math.round((diff / Math.abs(prev)) * 100);
    if (pct > 0) return { percent: `+${pct}%`, color: themeColor, bg: themeBg, arrow: 'up' };
    if (pct < 0) return { percent: `${pct}%`, color: '#ef4444', bg: 'rgba(239,68,68,0.08)', arrow: 'down' };
    return { percent: '0%', color: '#6b7280', bg: '#f3f4f6', arrow: 'up' };
  }

  const cards = [
    {
      key: 'present',
      label: 'Present',
      value: stats.presentToday,
      icon: (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: '#1EA7FF', flexShrink: 0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="7.5" r="3.5" fill="#162a16" />
            <path d="M2.5 19.5c0-3.59 2.91-6.5 6.5-6.5s6.5 2.91 6.5 6.5" fill="#162a16" />
            <path d="M15 12.5l2.5 2.5 5-5" stroke="#162a16" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      ),
      featured: true,
      valueColor: 'white',
      ...getPercentChange(stats.presentToday, prevStats.presentToday, '#16a34a', 'rgba(22,163,74,0.08)'),
      percentText: 'from last month',
      trendIcon: <Users size={10} color="#1EA7FF" />,
      trendText: `out of ${stats.totalActive} active`,
      trendColor: '#1EA7FF',
      trendSuffix: 'employees in office',
    },
    {
      key: 'absent',
      label: 'Absent',
      value: stats.absentToday,
      icon: (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: '#056098', flexShrink: 0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="7.5" r="3.5" fill="#fff" />
            <path d="M2.5 19.5c0-3.59 2.91-6.5 6.5-6.5s6.5 2.91 6.5 6.5" fill="#fff" />
            <line x1="16.5" y1="10" x2="21.5" y2="15" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="21.5" y1="10" x2="16.5" y2="15" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </span>
      ),
      featured: false,
      valueColor: '#111827',
      ...getPercentChange(stats.absentToday, prevStats.absentToday, '#ef4444', 'rgba(239,68,68,0.08)'),
      percentText: 'from last month',
      trendIcon: <TrendingDown size={10} color="#ef4444" />,
      trendText: `out of ${stats.totalActive} active`,
      trendColor: '#ef4444',
      trendSuffix: 'absent from office',
    },
    {
      key: 'leads',
      label: 'Total Leads',
      value: stats.totalLeads,
      icon: (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: '#056098', flexShrink: 0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="13" width="4" height="7" rx="1" fill="#fff" />
            <rect x="10" y="8" width="4" height="12" rx="1" fill="#fff" />
            <rect x="17" y="4" width="4" height="16" rx="1" fill="#fff" />
          </svg>
        </span>
      ),
      featured: false,
      valueColor: '#111827',
      ...getPercentChange(stats.totalLeads, prevStats.totalLeads, '#0891b2', 'rgba(8,145,178,0.08)'),
      percentText: 'from last month',
      trendIcon: <TrendingUp size={10} color="#0891b2" />,
      trendText: 'assigned',
      trendColor: '#0891b2',
      trendSuffix: 'leads total',
    },
    {
      key: 'projects',
      label: 'Active Projects',
      value: stats.activeProjects,
      icon: (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: '#056098', flexShrink: 0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="8" width="18" height="12" rx="2" fill="#fff" />
            <path d="M15 8V6a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" fill="none" />
            <line x1="8" y1="13" x2="16" y2="13" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.7" />
            <line x1="8" y1="17" x2="13" y2="17" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.5" />
          </svg>
        </span>
      ),
      featured: false,
      valueColor: '#111827',
      ...getPercentChange(stats.activeProjects, prevStats.activeProjects, '#16a34a', 'rgba(22,163,74,0.08)'),
      percentText: 'from last month',
      trendIcon: <Activity size={10} color="#16a34a" />,
      trendText: 'in progress',
      trendColor: '#16a34a',
      trendSuffix: 'this month',
    },

  ];

  let totalBreakMins = 0;
  if (todayAttendance?.breaks) {
    todayAttendance.breaks.forEach(b => {
      if (b.breakType !== 'Other') {
        const dur = b.duration !== undefined ? b.duration : Math.max(0, Math.floor((Date.now() - new Date(b.startTime).getTime()) / 60000));
        totalBreakMins += dur;
      }
    });
  }
  const limitMins = breakLimits?.totalBreakLimit || 60;
  const timeLeftMins = Math.max(0, limitMins - totalBreakMins);

  let statusText = '-';
  let statusBg = '#f3e8ff';
  let statusColor = '#7e22ce';
  let statusBorder = '#e9d5ff';

  if (todayAttendance?.logoutTime) {
    // Capitalize first letter for display
    if (todayAttendance.status) {
      statusText = todayAttendance.status.charAt(0).toUpperCase() + todayAttendance.status.slice(1);
      // For "half day" -> "Half Day"
      if (statusText.toLowerCase() === 'half day') statusText = 'Half Day';
    } else {
      statusText = 'Present';
    }

    const lowerStatus = statusText.toLowerCase();
    
    if (lowerStatus === 'present') {
      statusBg = '#e0f2fe'; statusColor = '#0284c7'; statusBorder = '#bae6fd';
    } else if (lowerStatus === 'half day' || lowerStatus === 'absent') {
      statusBg = '#fef2f2'; statusColor = '#ef4444'; statusBorder = '#fecaca';
    } else {
      statusBg = '#f8fafc'; statusColor = '#475569'; statusBorder = '#e2e8f0';
    }
  } else if (todayAttendance?.loginTime) {
    if (activeBreak) {
      statusText = 'On Break';
      statusBg = '#fffbeb'; statusColor = '#d97706'; statusBorder = '#fde68a';
    } else {
      statusText = 'Checked In';
      statusBg = '#eff6ff'; statusColor = '#2563eb'; statusBorder = '#bfdbfe';
    }
  }

  return (
    <div className="hr-page-wrapper" style={{ marginLeft: 0, width: '100vw', padding: '24px', boxSizing: 'border-box', backgroundColor: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="admin-dashboard" style={{ margin: 0, width: '100%' }}>
        {/* Company Header Card */}
        <div style={{
          background: '#fff', padding: '6px 24px', borderRadius: '16px', border: '1px solid #e2e8f0',
          marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/logo.png" alt="Talent Recruiter" style={{ width: '72px', height: '72px', objectFit: 'contain', marginRight: '16px' }} />
            <span style={{ fontSize: '32px', fontWeight: '600', letterSpacing: '-0.5px' }}>
              <span style={{ color: '#056098' }}>Talent</span>
              <span style={{ color: '#0f172a' }}>Recruiter</span>
            </span>
          </div>

          <button 
            onClick={() => {
              logout();
              navigate('/login');
            }}
            style={{ 
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', border: '1px solid #fecaca', 
              background: '#fef2f2', color: '#ef4444', fontSize: '14px', fontWeight: 600, cursor: 'pointer', borderRadius: '8px', transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fecaca'; }}
          >
            <LogOut size={16} /> Logout
          </button>
        </div>

        {/* Header */}
        <div className="admin-dashboard-header">
          <div className="admin-dashboard-title" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.02em' }}>
              Welcome back, {user?.firstName || (user?.name && user.name.split(' ')[0]) || 'User'} 👋
            </h2>
            <p style={{ margin: 0, marginTop: '4px', fontSize: '13.5px', color: '#64748b', fontWeight: '500' }}>
              Here's what's happening in your workspace today.
            </p>
          </div>
          <div className="hr-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* --- FANCY BREAK BADGES & BUTTONS --- */}
            {todayAttendance?.loginTime && !todayAttendance?.logoutTime && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => setBreakDetailsModal({ visible: true, type: 'All Breaks', breaks: todayAttendance.breaks || [] })}
                  style={{
                    background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', borderRadius: '999px', padding: '0 16px', height: '36px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s ease', cursor: 'pointer', fontSize: '13px', fontWeight: 600
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fef3c7'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fffbeb'; }}
                >
                  <Activity size={14} /> Break Details
                </button>
                {activeBreak ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      background: '#fffbeb', color: '#d97706', padding: '0 16px', height: '36px', borderRadius: '999px',
                      fontSize: '13.5px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      border: '1px solid #fde68a', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums'
                    }}>
                      <Hourglass size={14} /> {new Date(breakTimer * 1000).toISOString().substr(11, 8)}
                    </div>
                    <button
                      onClick={handleBreakEnd}
                      style={{
                        background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '999px', padding: '0 16px', height: '36px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s ease', cursor: 'pointer', fontSize: '13px', fontWeight: 600
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fecaca'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fee2e2'; }}
                    >
                      Stop Break
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowBreakModal(true)}
                    style={{
                      background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', borderRadius: '999px', padding: '0 16px', height: '36px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s ease', cursor: 'pointer', fontSize: '13px', fontWeight: 600
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fef3c7'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fffbeb'; }}
                  >
                    <Hourglass size={14} /> Take Break
                  </button>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginRight: '4px' }}>
              <span style={{ background: '#eff6ff', color: '#2563eb', padding: '0 16px', height: '36px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', border: '1px solid #bfdbfe', gap: '6px' }}>
                <Clock size={14} /> <span style={{ color: '#60a5fa', fontWeight: 500 }}>Check In:</span> {todayAttendance?.loginTime ? new Date(todayAttendance.loginTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
              </span>
              <span style={{ background: statusBg, color: statusColor, padding: '0 16px', height: '36px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', border: `1px solid ${statusBorder}`, gap: '6px' }}>
                <Activity size={14} /> <span style={{ opacity: 0.7, fontWeight: 500 }}>Status:</span> {statusText}
              </span>
            </div>

            {/* Check-in/Check-out Group */}
            {todayAttendance?.loginTime ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Timer in white */}
                <span style={{
                  background: '#ffffff', color: '#0f172a', padding: '0 16px', height: '36px', borderRadius: '999px',
                  fontSize: '13.5px', fontWeight: 600, display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums', gap: '6px'
                }}>
                  <Clock size={14} /> {new Date(checkInTimer * 1000).toISOString().substr(11, 8)}
                </span>
                
                {!todayAttendance?.logoutTime ? (
                  <button
                    onClick={handleCheckOut}
                    style={{
                      background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '999px', padding: '0 16px', height: '36px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fecaca'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fee2e2'; }}
                  >
                    <LogIn size={14} style={{ transform: 'rotate(180deg)' }} /> Check Out
                  </button>
                ) : (
                  <button
                    onClick={handleCheckIn}
                    style={{
                      background: '#e0f2fe', color: '#0284c7', border: 'none', borderRadius: '999px', padding: '0 16px', height: '36px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#bae6fd'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#e0f2fe'; }}
                  >
                    <LogIn size={14} /> Check In
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={handleCheckIn}
                style={{
                  background: '#e0f2fe', color: '#0284c7', border: 'none', borderRadius: '999px', padding: '0 16px', height: '36px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#bae6fd'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#e0f2fe'; }}
              >
                <LogIn size={14} /> Check In
              </button>
            )}
          </div>{/* end hr-header-actions */}
        </div>{/* end admin-dashboard-header */}

        
        {activeView === 'main' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '20px', padding: '24px 0', flex: 1, alignContent: 'start' }}>
            
            <Link to="/" className="hr-module-card">
              <LayoutDashboard size={32} color="#056098" style={{ marginBottom: 8 }} />
              <span style={tileTextStyle}>Dashboard</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Overview & Stats</span>
            </Link>

            {hasPermission('calendar', 'read') && (
              <Link to="/calendar" className="hr-module-card">
                <Calendar size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Calendar</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Company Events</span>
              </Link>
            )}

            {hasPermission('broadcasts', 'read') && (
              <Link to="/broadcasts" className="hr-module-card" style={{ position: 'relative' }}>
                <Megaphone size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Announcement</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Company Notices</span>
                {renderBadge(unviewedBroadcasts + unreadAnnouncementNotificationsCount)}
              </Link>
            )}

            {hasPermission('clients', 'read') && (
              <Link to="/clients" className="hr-module-card" style={{ position: 'relative' }}>
                <Handshake size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Clients</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Manage Clients</span>
                {renderBadge(unreadClientNotificationsCount)}
              </Link>
            )}

            {hasPermission('accounts', 'read') && (
              <Link to="/client-payments" className="hr-module-card">
                <Wallet size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Accounts</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Financial Records</span>
              </Link>
            )}

            {hasPermission('proposals', 'read') && (
              <Link to="/proposals" className="hr-module-card">
                <FileText size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Proposals</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Manage Proposals</span>
              </Link>
            )}

            {hasPermission('goals', 'read') && (
              <Link to="/goals" className="hr-module-card" style={{ position: 'relative' }}>
                <Target size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Tasks</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Track Progress</span>
                {renderBadge(unreadTaskNotificationsCount)}
              </Link>
            )}

            {hasPermission('assets', 'read') && (
              <Link to="/assets" className="hr-module-card">
                <Package size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Assets</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Company Assets</span>
              </Link>
            )}

            {hasPermission('leaves', 'read') && (
              <Link to="/leaves" className="hr-module-card" style={{ position: 'relative' }}>
                <Umbrella size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Leaves</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Manage Leaves</span>
                {renderBadge(unreadLeaveNotificationsCount)}
              </Link>
            )}

            {hasPermission('attendance', 'read') && (
              <Link to="/attendance" className="hr-module-card">
                <Clock size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Attendance</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Track Time</span>
              </Link>
            )}

            {hasPermission('activities', 'read') && (
              <Link to="/activities" className="hr-module-card">
                <Activity size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Activities</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Recent Actions</span>
              </Link>
            )}

            {(hasPermission('leads', 'read') || hasPermission('leads', 'create')) && (
              <Link to="/leads" className="hr-module-card">
                <UserCog size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>My Leads</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Lead Management</span>
              </Link>
            )}

            {hasPermission('employees', 'read') && (
              <Link to="/employees" className="hr-module-card">
                <Users size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Employees</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Team Directory</span>
              </Link>
            )}

            {hasPermission('documents', 'read') && (
              <Link to="/documents" className="hr-module-card">
                <FolderOpen size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Documents</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Company Files</span>
              </Link>
            )}

            {hasPermission('my-documents', 'read') && (
              <Link to="/my-documents" className="hr-module-card">
                <FileText size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>My Documents</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Personal Files</span>
              </Link>
            )}

            {hasPermission('roles', 'read') && (
              <Link to="/roles" className="hr-module-card">
                <Shield size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>User & Roles</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Access Control</span>
              </Link>
            )}

            {hasPermission('payroll', 'read') && (
              <Link to="/payroll" className="hr-module-card">
                <Wallet size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Payroll</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Salary & Wages</span>
              </Link>
            )}

            {hasPermission('my-payslips', 'read') && (
              <Link to="/my-payroll" className="hr-module-card">
                <Receipt size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>My Payslip</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Salary Slips</span>
              </Link>
            )}

            {hasPermission('recruitment', 'read') && (
              <div onClick={() => setActiveView('recruitment')} className="hr-module-card">
                <Building2 size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Recruit</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Hiring Pipeline</span>
              </div>
            )}

            <Link to="/notifications" className="hr-module-card" style={{ position: 'relative' }}>
              <Bell size={32} color="#056098" style={{ marginBottom: 8 }} />
              <span style={tileTextStyle}>Notifications</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Recent Alerts</span>
              {renderBadge(unreadNotificationsCount)}
            </Link>

            {user?.role?.name === 'Admin' && (
              <Link to="/settings" className="hr-module-card">
                <Settings size={32} color="#056098" style={{ marginBottom: 8 }} />
                <span style={tileTextStyle}>Settings</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>System Config</span>
              </Link>
            )}
        </div>
        )}

        {activeView === 'recruitment' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '20px', padding: '24px 0', flex: 1, alignContent: 'start' }}>
            <div onClick={() => setActiveView('main')} className="hr-module-card" style={{ background: '#f8fafc', border: '1px dashed #cbd5e1' }}>
              <ArrowLeft size={32} color="#475569" style={{ marginBottom: 8 }} />
              <span style={{...tileTextStyle, color: '#475569'}}>Back to Modules</span>
                <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Main Dashboard</span>
                <span style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Main Dashboard</span>
            </div>
            <Link to="/recruitment/dashboard" className="hr-module-card">
              <LayoutDashboard size={32} color="#056098" style={{ marginBottom: 8 }} />
              <span style={tileTextStyle}>Dashboard</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Overview & Stats</span>
            </Link>
            <Link to="/recruitment/jobs" className="hr-module-card">
              <Briefcase size={32} color="#056098" style={{ marginBottom: 8 }} />
              <span style={tileTextStyle}>Jobs</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Manage Openings</span>
            </Link>
            <Link to="/recruitment/job-applications" className="hr-module-card">
              <Users size={32} color="#056098" style={{ marginBottom: 8 }} />
              <span style={tileTextStyle}>Job Applications</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Review Candidates</span>
            </Link>
            <Link to="/recruitment/interview-schedule" className="hr-module-card">
              <Calendar size={32} color="#056098" style={{ marginBottom: 8 }} />
              <span style={tileTextStyle}>Interview Schedule</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Plan Interviews</span>
            </Link>
            <Link to="/recruitment/letters" className="hr-module-card">
              <FileText size={32} color="#056098" style={{ marginBottom: 8 }} />
              <span style={tileTextStyle}>Letters</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Offer & Rejection</span>
            </Link>
            <Link to="/recruitment/candidate-database" className="hr-module-card">
              <UserCog size={32} color="#056098" style={{ marginBottom: 8 }} />
              <span style={tileTextStyle}>Candidate Database</span>
                <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Talent Pool</span>
            </Link>
        </div>
        )}

        {/* Recent Activities Section */}
        <div style={{ marginTop: '8px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#0f172a' }}>Recent Activities</h3>
            <Activity size={20} color="#94a3b8" />
          </div>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '280px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 24px', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Activity</th>
                  <th style={{ padding: '12px 24px', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</th>
                  <th style={{ padding: '12px 24px', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {recentActivities.map((activity, idx) => {
                  const actStyle = getActivityStyle(activity.type);
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '16px 24px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '999px', background: actStyle.bg, color: actStyle.color, fontSize: '13px', fontWeight: 600 }}>
                          <span>{activity.icon || getActivityIcon(activity.type, actStyle.color)}</span>
                          {activity.title}
                        </span>
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#334155', fontWeight: 500 }}>
                        {activity.description || '-'}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {formatTimeAgo(activity.timestamp)}
                      </td>
                    </tr>
                  );
                })}
                {recentActivities.length === 0 && (
                  <tr>
                    <td colSpan="3" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                      No recent activities found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>{/* end outer layout */}


      {/* Break Selection Modal */}
      {showBreakModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)' }}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '90%', maxWidth: '420px', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#0f172a', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Timer size={20} color="#f59e0b" /> Take a Break
            </h3>
            <form onSubmit={handleBreakStart}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>Break Type <span style={{ color: '#ef4444' }}>*</span></label>
                <select
                  value={breakForm.type}
                  onChange={(e) => setBreakForm({ ...breakForm, type: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: '#f8fafc', outline: 'none' }}
                >
                  <option value="Tea Break">Tea Break</option>
                  <option value="Lunch Break">Lunch Break</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {breakForm.type === 'Other' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>Reason <span style={{ color: '#ef4444' }}>*</span></label>
                  <textarea
                    rows={3}
                    value={breakForm.reason}
                    onChange={(e) => setBreakForm({ ...breakForm, reason: e.target.value })}
                    placeholder="Enter reason for break"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: '#f8fafc', outline: 'none', resize: 'vertical' }}
                    required
                  ></textarea>
                </div>
              )}


              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button type="button" onClick={() => setShowBreakModal(false)} style={{ padding: '8px 16px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 16px', border: 'none', background: '#f59e0b', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Start Break</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Break Details Info Modal */}
      {breakDetailsModal.visible && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)' }} onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 8px 32px rgba(15,23,42,0.13)', padding: '24px', width: '400px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: '0', fontSize: '18px', color: '#0f172a' }}>{breakDetailsModal.type} Details</h3>
              <button onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <XCircle size={20} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <Clock size={24} strokeWidth={1.5} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Time Remaining</span>
                  <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Time Taken</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: '20px', color: '#0f172a', fontWeight: 600, letterSpacing: '-0.5px' }}>{timeLeftMins} <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500, letterSpacing: '0' }}>mins</span></span>
                  <span style={{ fontSize: '16px', color: '#475569', fontWeight: 600 }}>{totalBreakMins} <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500 }}>mins</span></span>
                </div>
              </div>
            </div>

            {breakDetailsModal.breaks && breakDetailsModal.breaks.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {breakDetailsModal.breaks.map((b, idx) => {
                  const dur = b.duration !== undefined ? b.duration : Math.max(0, Math.floor((Date.now() - new Date(b.startTime).getTime()) / 60000));
                  return (
                    <div key={idx} style={{ padding: '16px', background: '#fff', borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 4px rgba(15,23,42,0.02)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: b.endTime ? '#cbd5e1' : '#10b981' }}></div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>{b.breakType || `Break ${idx + 1}`}</div>
                          <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Hourglass size={12} />
                            {new Date(b.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {' - '}
                            {b.endTime ? new Date(b.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                        {dur} mins
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>No breaks taken yet.</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })}
                style={{ padding: '8px 16px', border: 'none', background: '#10b981', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Break Glassmorphism Screen */}
      {activeBreak && !hideBreakScreen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.4s ease'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '24px', opacity: 0.8 }}>
              <Clock size={40} color="#334155" strokeWidth={1.5} />
            </div>
            <h1 style={{ fontSize: '26px', color: '#0f172a', margin: '0 0 6px 0', fontWeight: 600, letterSpacing: '-0.3px' }}>On Break</h1>
            <p style={{ fontSize: '15px', color: '#475569', margin: '0 0 36px 0', fontWeight: 500 }}>{activeBreak.type}</p>

            <div style={{ fontSize: '64px', fontWeight: '300', color: '#0f172a', fontVariantNumeric: 'tabular-nums', margin: '0 0 44px 0', letterSpacing: '-1.5px' }}>
              {new Date(breakTimer * 1000).toISOString().substr(11, 8)}
            </div>

            <button
              onClick={() => setHideBreakScreen(true)}
              style={{ padding: '12px 40px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 158, 11, 0.35)'; e.currentTarget.style.background = '#d97706'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.25)'; e.currentTarget.style.background = '#f59e0b'; }}
            >
              Resume Work
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default HRDashboard;
